// frontend/src/pages/Recepcion/PantallaInformativaPage.js
import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import ColaEspera from '../../components/pantallaInformativa/ColaEspera';
import ColaRotativa from '../../components/pantallaInformativa/ColaRotativa';
import RelojPantalla from '../../components/pantallaInformativa/RelojPantalla';
import VideoPantalla from '../../components/pantallaInformativa/VideoPantalla';
import { ESTADO_INICIAL_COLA, reducirCola } from '../../components/pantallaInformativa/colaDeVideos';
import { enviarLatido, obtenerPantalla } from '../../services/pantallaInformativaService';
import '../../style/pages/PantallaInformativa.css';

const INTERVALO_ACTUALIZACION_MS = 10000;
// El latido trae el control remoto: una pausa desde Atención tarda como mucho esto en llegar.
const INTERVALO_LATIDO_MS = 1000;
// Si YouTube no pudo pasar ninguno de los videos, se vuelve a probar al rato.
const MS_REINTENTO_VIDEOS = 60000;
const MS_BARRA_VISIBLE_AL_ABRIR = 10000;
const MS_BARRA_VISIBLE = 3000;
const SIN_VIDEOS = [];
// Las colas que se alternan en el segundo lugar, en este orden. Mismos íconos que AREAS_ATENCION.
const COLAS_ROTATIVAS = [
    { clave: 'ANSES', titulo: 'ANSES', icono: 'bi-building', campo: 'colaAnses' },
    { clave: 'COBROS', titulo: 'Cobros', icono: 'bi-cash-coin', campo: 'colaCobros' },
    { clave: 'TRIBUNALES', titulo: 'Tribunales', icono: 'bi-bank', campo: 'colaTribunales' },
];

/** La clave viaja en el fragmento del link (#clave=…): el fragmento no se manda al servidor. */
function leerClaveDelLink() {
    return new URLSearchParams(window.location.hash.replace(/^#/, '')).get('clave') || '';
}

/**
 * La página de configuración muestra el TV achicado dentro de un iframe (#…&vistaPrevia=1): ahí
 * no va la barra de control y el video nunca suena, aunque esté configurado con sonido.
 */
function esVistaPreviaDelLink() {
    return new URLSearchParams(window.location.hash.replace(/^#/, '')).get('vistaPrevia') === '1';
}

/**
 * Pantalla informativa: el TV de la sala de espera.
 *
 * Es una página de kiosco. Va fuera de AuthProvider y de MainLayout (ver App.js): no muestra nada
 * del sistema y no depende de una sesión. Se abre desde Atención → Pantalla informativa, que arma
 * el link con la clave, y se lleva al TV de dos formas:
 *  - HDMI (el TV es el segundo monitor de recepción): "Mostrar en el TV" la pone a pantalla
 *    completa directamente en el otro monitor (Window Management API de Chrome).
 *  - Chromecast: "Pantalla completa" y después Chrome → Transmitir → Transmitir pestaña.
 *
 * Según el modo configurado muestra las colas y el video (Mixto), solo el video o solo las colas.
 * Los videos se pasan en cola (ver colaDeVideos.js) y se controlan solo desde la configuración
 * (Atención → Pantalla informativa), nunca desde el TV: cada pocos segundos el TV manda un latido
 * con lo que está pasando y recibe de vuelta el control remoto.
 */
const PantallaInformativaPage = () => {
    const [clave, setClave] = useState(leerClaveDelLink);
    const [esVistaPrevia] = useState(esVistaPreviaDelLink);
    const [pantalla, setPantalla] = useState(null);
    // 'cargando' | 'ok' | 'reconectando' | 'no-autorizada'
    const [estadoConexion, setEstadoConexion] = useState('cargando');
    const [barraVisible, setBarraVisible] = useState(true);
    const [interactuo, setInteractuo] = useState(() => Boolean(navigator.userActivation?.hasBeenActive));
    const [pantallaCompleta, setPantallaCompleta] = useState(() => Boolean(document.fullscreenElement));
    const [hayOtraPantalla, setHayOtraPantalla] = useState(false);
    const [aviso, setAviso] = useState('');
    const [cola, despacharCola] = useReducer(reducirCola, ESTADO_INICIAL_COLA);
    const [controlDelLatido, setControlDelLatido] = useState(null);
    // El título lo informa el reproductor cuando arranca cada video; va en el latido.
    const tituloRef = useRef({ idVideo: null, titulo: '' });
    const latidoRef = useRef(null);
    const latirYaRef = useRef(null);

    const videos = pantalla?.videos ?? SIN_VIDEOS;
    const videosRef = useRef(videos);
    const firmaVideos = videos.map((video) => `${video.id}:${video.url}`).join('\n');
    const modo = pantalla?.modo || 'MIXTO';
    const soloVideo = modo === 'SOLO_VIDEO';
    const soloTurnero = modo === 'SOLO_TURNERO';
    const indiceActual = videos.findIndex((video) => video.id === cola.idActual);
    const videoActual = indiceActual === -1 ? null : videos[indiceActual];
    const fallaronTodos = videos.length > 0 && cola.erroresSeguidos >= videos.length;

    let estadoVideo = 'REPRODUCIENDO';
    if (soloTurnero || !videoActual) estadoVideo = 'SIN_VIDEO';
    else if (fallaronTodos) estadoVideo = 'ERROR';
    else if (cola.pausado) estadoVideo = 'PAUSADO';

    /** Las acciones que mueven la cola necesitan la lista de videos al momento de aplicarse. */
    const despachar = useCallback((accion) => despacharCola({ ...accion, videos: videosRef.current }), []);

    // Llegó otra lista (al abrir o porque se guardó la configuración).
    useEffect(() => {
        videosRef.current = videos;
        despachar({ tipo: 'LISTA' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firmaVideos, despachar]);

    // El control remoto llega por dos lados (el GET de cada 10 s y el latido de cada 3 s); la cola
    // aplica cada comando una sola vez, venga por donde venga. Va después de la lista, para que un
    // video recién agregado ya esté.
    const secuenciaPantalla = pantalla?.control?.secuencia;
    useEffect(() => {
        if (pantalla?.control) despachar({ tipo: 'CONTROL', control: pantalla.control });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secuenciaPantalla, despachar]);
    useEffect(() => {
        if (controlDelLatido) despachar({ tipo: 'CONTROL', control: controlDelLatido });
    }, [controlDelLatido, despachar]);

    useEffect(() => {
        if (!fallaronTodos) return undefined;
        const reintento = setTimeout(() => despachar({ tipo: 'REINTENTAR' }), MS_REINTENTO_VIDEOS);
        return () => clearTimeout(reintento);
    }, [fallaronTodos, despachar]);

    useEffect(() => {
        latidoRef.current = {
            idVideo: estadoVideo === 'SIN_VIDEO' ? null : cola.idActual,
            titulo: tituloRef.current.idVideo === cola.idActual ? tituloRef.current.titulo : '',
            estado: estadoVideo,
            secuenciaControl: cola.secuenciaControl,
        };
    });

    // Apenas se aplica una orden de Atención, se avisa sin esperar al próximo latido: así la
    // configuración confirma la pausa enseguida.
    useEffect(() => {
        if (cola.secuenciaControl !== null) latirYaRef.current?.();
    }, [cola.secuenciaControl]);

    const idActual = cola.idActual;
    const alArrancarVideo = useCallback((titulo) => {
        tituloRef.current = { idVideo: idActual, titulo };
        if (latidoRef.current?.idVideo === idActual) latidoRef.current = { ...latidoRef.current, titulo };
        despachar({ tipo: 'ARRANCO' });
    }, [idActual, despachar]);
    const alTerminarVideo = useCallback(() => despachar({ tipo: 'TERMINO' }), [despachar]);
    const alFallarVideo = useCallback(() => despachar({ tipo: 'ERROR' }), [despachar]);

    useEffect(() => {
        const tituloAnterior = document.title;
        document.title = 'Pantalla informativa';
        document.body.classList.add('pantalla-informativa-activa');
        return () => {
            document.title = tituloAnterior;
            document.body.classList.remove('pantalla-informativa-activa');
        };
    }, []);

    // Si desde Atención se vuelve a abrir la pantalla con un link nuevo, la ventana ya abierta
    // (mismo nombre) solo cambia el fragmento: no se recarga, así que hay que releer la clave.
    useEffect(() => {
        const alCambiarLink = () => setClave(leerClaveDelLink());
        window.addEventListener('hashchange', alCambiarLink);
        return () => window.removeEventListener('hashchange', alCambiarLink);
    }, []);

    useEffect(() => {
        let activo = true;
        const cargar = async () => {
            try {
                const datos = await obtenerPantalla(clave);
                if (!activo) return;
                setPantalla(datos);
                setEstadoConexion('ok');
            } catch (e) {
                if (!activo) return;
                const status = e?.response?.status;
                if (status === 401 || status === 403) {
                    // Clave regenerada (o link incompleto): se dejan de mostrar los nombres.
                    setPantalla(null);
                    setEstadoConexion('no-autorizada');
                } else {
                    // Corte de red o backend reiniciando: se siguen mostrando los últimos datos.
                    setEstadoConexion((anterior) => (anterior === 'no-autorizada' ? anterior : 'reconectando'));
                }
            }
        };
        cargar();
        const intervalo = setInterval(cargar, INTERVALO_ACTUALIZACION_MS);
        return () => {
            activo = false;
            clearInterval(intervalo);
        };
    }, [clave]);

    // Latido (cada segundo): cuenta a la configuración qué está pasando y trae el control remoto. La
    // vista previa no late (no es el TV) y toma el control solo del GET de cada 10 s.
    const noAutorizada = estadoConexion === 'no-autorizada';
    useEffect(() => {
        if (esVistaPrevia || !clave || noAutorizada) return undefined;
        let activo = true;
        // De a un latido por vez: si el backend tarda, no se apilan. El que se pide al aplicar una orden
        // (urgente) no se pierde: sale apenas vuelve el que estaba en camino, con el estado más nuevo.
        let enCamino = false;
        let pendiente = false;
        const latir = async (urgente = false) => {
            if (enCamino) {
                if (urgente) pendiente = true;
                return;
            }
            enCamino = true;
            try {
                const control = await enviarLatido(clave, latidoRef.current);
                if (activo && control) {
                    setControlDelLatido((anterior) => (anterior?.secuencia === control.secuencia ? anterior : control));
                }
            } catch {
                // El GET de cada 10 s ya muestra "Reconectando…" o "Pantalla no autorizada".
            } finally {
                enCamino = false;
            }
            if (activo && pendiente) {
                pendiente = false;
                latir();
            }
        };
        latir();
        latirYaRef.current = () => latir(true);
        const intervalo = setInterval(() => latir(), INTERVALO_LATIDO_MS);
        return () => {
            activo = false;
            latirYaRef.current = null;
            clearInterval(intervalo);
        };
    }, [clave, esVistaPrevia, noAutorizada]);

    // Barra de control y cursor: aparecen al mover el mouse y se esconden solos, para que en el TV
    // no quede nada encima de la pantalla.
    useEffect(() => {
        let temporizador = setTimeout(() => setBarraVisible(false), MS_BARRA_VISIBLE_AL_ABRIR);
        const mostrar = () => {
            setBarraVisible(true);
            clearTimeout(temporizador);
            temporizador = setTimeout(() => setBarraVisible(false), MS_BARRA_VISIBLE);
        };
        window.addEventListener('mousemove', mostrar);
        window.addEventListener('touchstart', mostrar, { passive: true });
        window.addEventListener('keydown', mostrar);
        return () => {
            clearTimeout(temporizador);
            window.removeEventListener('mousemove', mostrar);
            window.removeEventListener('touchstart', mostrar);
            window.removeEventListener('keydown', mostrar);
        };
    }, []);

    // El navegador solo deja reproducir con sonido después de un toque o una tecla en la página.
    // Hasta entonces el video arranca silenciado (si no, no arranca).
    useEffect(() => {
        if (interactuo) return undefined;
        const marcar = () => setInteractuo(true);
        window.addEventListener('pointerdown', marcar, { once: true });
        window.addEventListener('keydown', marcar, { once: true });
        return () => {
            window.removeEventListener('pointerdown', marcar);
            window.removeEventListener('keydown', marcar);
        };
    }, [interactuo]);

    useEffect(() => {
        const actualizar = () => setPantallaCompleta(Boolean(document.fullscreenElement));
        document.addEventListener('fullscreenchange', actualizar);
        return () => document.removeEventListener('fullscreenchange', actualizar);
    }, []);

    // "Mostrar en el TV" solo tiene sentido si Chrome ve un segundo monitor.
    useEffect(() => {
        if (!('getScreenDetails' in window) || !window.screen) return undefined;
        const actualizar = () => setHayOtraPantalla(Boolean(window.screen.isExtended));
        actualizar();
        window.screen.addEventListener?.('change', actualizar);
        return () => window.screen.removeEventListener?.('change', actualizar);
    }, []);

    // Que el TV no se apague ni active el protector de pantalla mientras la página está visible.
    useEffect(() => {
        if (!('wakeLock' in navigator)) return undefined;
        let bloqueo = null;
        const pedir = async () => {
            if (document.visibilityState !== 'visible') return;
            try {
                bloqueo = await navigator.wakeLock.request('screen');
            } catch {
                // Sin permiso (o sin HTTPS): la pantalla sigue andando, solo depende de la config del equipo.
            }
        };
        pedir();
        document.addEventListener('visibilitychange', pedir);
        return () => {
            document.removeEventListener('visibilitychange', pedir);
            bloqueo?.release?.().catch(() => {});
        };
    }, []);

    const mostrarEnTv = useCallback(async () => {
        setAviso('');
        let detalles;
        try {
            // La primera vez Chrome pide el permiso "Administración de ventanas".
            detalles = await window.getScreenDetails();
        } catch {
            setAviso('Chrome no tiene permiso para usar el segundo monitor. Habilitá "Administración de ventanas" en el candado de la barra de direcciones.');
            return;
        }
        const otraPantalla = detalles.screens.find((s) => s !== detalles.currentScreen) || detalles.currentScreen;
        try {
            await document.documentElement.requestFullscreen({ screen: otraPantalla });
        } catch {
            // Si hubo que aceptar el permiso, el gesto del clic ya venció: hace falta un clic nuevo.
            setAviso('Tocá "Mostrar en el TV" de nuevo para terminar de pasar la pantalla al TV.');
        }
    }, []);

    const alternarPantallaCompleta = useCallback(async () => {
        setAviso('');
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            } else {
                await document.documentElement.requestFullscreen();
            }
        } catch {
            setAviso('El navegador no permitió la pantalla completa. Probá con la tecla F11.');
        }
    }, []);

    if (!clave || estadoConexion === 'no-autorizada') {
        return (
            <div className="pi-pantalla pi-pantalla--mensaje">
                <i className="bi bi-shield-lock pi-mensaje__icono" aria-hidden="true" />
                <h1 className="pi-mensaje__titulo">Pantalla no autorizada</h1>
                <p className="pi-mensaje__texto">
                    Abrila de nuevo desde Atención → Pantalla informativa.
                </p>
            </div>
        );
    }

    const colaGeneral = pantalla?.colaGeneral ?? [];
    // La cola que no tiene a nadie no se muestra.
    const colasRotativas = COLAS_ROTATIVAS
        .map(({ campo, ...cola }) => ({ ...cola, turnos: pantalla?.[campo] ?? [] }))
        .filter((cola) => cola.turnos.length > 0);
    const mensaje = pantalla?.mensaje?.trim();
    const reconectando = estadoConexion === 'reconectando' && (
        <>
            <i className="bi bi-wifi-off" aria-hidden="true" /> Reconectando…
        </>
    );
    const claseCuerpo = soloVideo ? ' pi-cuerpo--solo-video' : soloTurnero ? ' pi-cuerpo--solo-turnero' : '';

    return (
        <div className={`pi-pantalla${barraVisible ? '' : ' pi-pantalla--sin-cursor'}`}>
            {!soloVideo && (
                <header className="pi-encabezado">
                    <span className="pi-encabezado__marca">
                        <i className="bi bi-bank2" aria-hidden="true" />
                        Estudio Jurídico Previsional
                    </span>
                    <h1 className="pi-encabezado__titulo">Turnos en espera</h1>
                    <span className="pi-encabezado__estado">
                        {reconectando}
                        {soloTurnero && <RelojPantalla variante="encabezado" />}
                    </span>
                </header>
            )}
            {soloVideo && reconectando && <span className="pi-aviso-conexion">{reconectando}</span>}

            <main className={`pi-cuerpo${claseCuerpo}`}>
                {!soloVideo && (
                    <div className="pi-colas">
                        {/* Mitad y mitad, haya la gente que haya en cada una (también vacía). Sin
                            nadie en ANSES, Cobros ni Tribunales, la general ocupa todo. */}
                        <ColaEspera titulo="Atención general" icono="bi-people-fill" turnos={colaGeneral} />
                        <ColaRotativa grupos={colasRotativas} />
                    </div>
                )}
                {!soloTurnero && (
                    <div className="pi-lateral">
                        <VideoPantalla
                            video={fallaronTodos ? null : videoActual}
                            reproduccion={cola.reproduccion}
                            pausado={cola.pausado}
                            conSonido={Boolean(pantalla?.videoConSonido) && interactuo && !esVistaPrevia}
                            onTermino={alTerminarVideo}
                            onError={alFallarVideo}
                            onTitulo={alArrancarVideo}
                        />
                        {!soloVideo && <RelojPantalla />}
                    </div>
                )}
            </main>

            {mensaje && (
                <footer className="pi-pie">
                    <span className="pi-pie__etiqueta">
                        <i className="bi bi-megaphone-fill" aria-hidden="true" /> Aviso
                    </span>
                    <div className="pi-pie__marquesina">
                        {/* Velocidad pareja: un mensaje largo tarda más en cruzar, no corre más rápido. */}
                        <span style={{ animationDuration: `${14 + mensaje.length * 0.12}s` }}>{mensaje}</span>
                    </div>
                </footer>
            )}

            {!esVistaPrevia && (
                <div className={`pi-control${barraVisible ? ' pi-control--visible' : ''}`}>
                    {hayOtraPantalla && (
                        <button type="button" className="pi-control__boton pi-control__boton--principal" onClick={mostrarEnTv}>
                            <i className="bi bi-tv" aria-hidden="true" /> Mostrar en el TV
                        </button>
                    )}
                    <button type="button" className="pi-control__boton" onClick={alternarPantallaCompleta}>
                        <i className={`bi ${pantallaCompleta ? 'bi-fullscreen-exit' : 'bi-arrows-fullscreen'}`} aria-hidden="true" />
                        {pantallaCompleta ? ' Salir de pantalla completa' : ' Pantalla completa'}
                    </button>
                    {aviso && <span className="pi-control__aviso">{aviso}</span>}
                </div>
            )}
        </div>
    );
};

export default PantallaInformativaPage;
